/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  JobSpecTemplateClauseDto,
  JobSpecTemplateDetailsDto,
  JobSpecTemplateFilterDto,
  JobSpecTemplateListItemDto,
  JobSpecTemplateParagraphBaseDto,
  JobSpecTemplateParagraphDto,
  JobSpecTemplateSectionBaseDto,
  JobSpecTemplateSectionDto,
  SpecTemplateClauses,
  SpecTemplateStatus,
} from '@gh/builder-shared-data';
import {
  composeSetters,
  createArrayHelper,
  entityValidatorByEntityConfig,
  fieldStateByEntityConfig,
  fieldValidationSetter,
  fieldValidatorByEntityConfig,
  formStateSnapshot,
  FormStateSnapshot,
  getFormStateSnapshotValue,
  hasFormStateError,
  isEntityInvalid,
  setEntityField,
  setFormStateSnapshotValue,
  TextTypes,
} from '@gh/common-shared';
import { EntityVersion, ID, WithEntityState } from '@gh/common-shared-data';
import { createTreeHelper } from '@gh/core-ui-ext/tree';
import { FocusedCell } from '@gh/core-ui-grid';
import { compact, map, omit, pick, uniqueId } from 'lodash';

export type JobSpecTemplateListItem = JobSpecTemplateListItemDto;
export type JobSpecTemplateFilter = JobSpecTemplateFilterDto;

export type JstMasterInfo = {
  name: string;
  memo: string;
};

export enum JobSpecTemplateLevel { Paragraph = 1, Section = 2 }

export type JstSelection = {
  readonly level?: JobSpecTemplateLevel;
  readonly paragraph?: JstParagraph;
  readonly section?: JstSection;
  readonly clauseFocusedCell?: FocusedCell;
};

export type JstParagraphInfo = JobSpecTemplateParagraphBaseDto;
export type JstParagraph = {
  id: ID;
  specTemplateParagraphId?: ID;
  info: FormStateSnapshot<JstParagraphInfo>;
  sections: JstSection[];
  isNew?: boolean;
};

export type JstSectionInfo = JobSpecTemplateSectionBaseDto;
export type JstSection = {
  id: ID;
  specTemplateSectionId?: ID;
  info: FormStateSnapshot<JstSectionInfo>;
  clausesAre: SpecTemplateClauses;
  clauses: JstClause[];
  isNew?: boolean;
};

export type JstElement = JstParagraph | JstSection;

export type JstClause = WithEntityState<JobSpecTemplateClauseDto> & { id: ID };

export type JstDetails = {
  specTemplateId?: ID;
  version?: EntityVersion;
  status: SpecTemplateStatus;
  masterInfo: FormStateSnapshot<JstMasterInfo>;
  isForRfq: boolean;
  paragraphs: JstParagraph[];
};

export const JST_CLAUSE_ENTITY_CONFIG = {
  text: { required: true, type: TextTypes.Unlimited },
};

export const getJstClauseId = (clause: JstClause) => clause.id;

export const jstDetailsFromDto = (dto: JobSpecTemplateDetailsDto) => <JstDetails>({
  specTemplateId: dto.specTemplateId,
  version: dto.version,
  status: dto.status,
  masterInfo: formStateSnapshot(pick(dto, ['name', 'memo'])),
  isForRfq: dto.isForRfq,
  paragraphs: map(dto.paragraphs, jstParagraphFromDto),
});

const jstParagraphFromDto = (dto: JobSpecTemplateParagraphDto) => <JstParagraph>({
  id: uniqueId('jst-paragraph:'),
  specTemplateParagraphId: dto.specTemplateParagraphId,
  info: formStateSnapshot(pick(dto, ['name', 'text', 'paragraphIs', 'textIs', 'isTextWarning'])),
  sections: map(dto.sections, jstSectionFromDto),
});

const jstSectionFromDto = (dto: JobSpecTemplateSectionDto) => <JstSection>({
  id: uniqueId('jst-section:'),
  specTemplateSectionId: dto.specTemplateSectionId,
  info: formStateSnapshot(pick(dto, ['name', 'text', 'sectionIs', 'textIs', 'isTextWarning'])),
  clausesAre: dto.clausesAre,
  clauses: map(dto.clauses, (clause) => ({ ...clause, id: uniqueId('jst-clause:') })),
});

export const jstDetailsToDto = ({
                                  specTemplateId, version, status, masterInfo, isForRfq, paragraphs,
                                }: JstDetails) => <JobSpecTemplateDetailsDto>({
  specTemplateId, version, status, isForRfq,
  ...getFormStateSnapshotValue(masterInfo),
  paragraphs: paragraphs.map(jstParagraphToDto),
});

const jstParagraphToDto = ({
                             specTemplateParagraphId, isNew, info, sections,
                           }: JstParagraph) => <JobSpecTemplateParagraphDto>({
  specTemplateParagraphId: isNew ? void 0 : specTemplateParagraphId, // clear ID for new paragraph
  ...getFormStateSnapshotValue(info),
  sections: sections.map(jstSectionToDto),
});

const jstSectionToDto = ({
                           specTemplateSectionId, isNew, info, clauses, clausesAre,
                         }: JstSection) => <JobSpecTemplateSectionDto>({
  specTemplateSectionId: isNew ? void 0 : specTemplateSectionId, // clear ID for new section
  ...getFormStateSnapshotValue(info),
  clausesAre,
  clauses: clauses.map(jstClauseToDto),
});

const jstClauseToDto = (clause: JstClause) => <JobSpecTemplateClauseDto>(omit(clause, ['id', 'entityState']));

const newJstDetailsMasterInfo = () => formStateSnapshot({
  name: '',
  memo: '',
});

export const newJstDetails = () => <JstDetails>({
  masterInfo: newJstDetailsMasterInfo(),
  isForRfq: false,
  paragraphs: [],
  status: SpecTemplateStatus.Active,
});

export const toClonedJstSection = (section: JstSection) => ({
  ...section,
  specTemplateSectionId: void 0,
});

export const toClonedJstParagraph = (paragraph: JstParagraph) => ({
  ...paragraph,
  specTemplateParagraphId: void 0,
  sections: map(paragraph.sections, toClonedJstSection),
});

export const toClonedJstDetails = (details: JstDetails) => ({
  ...details,
  masterInfo: setFormStateSnapshotValue(details.masterInfo, (info) => ({ ...info, name: '' })),
  paragraphs: map(details.paragraphs, toClonedJstParagraph),
  isForRfq: false,
  specTemplateId: void 0,
});

export const jstTreeHelper = createTreeHelper(['sections']);

export const jstClauseArr = createArrayHelper(getJstClauseId);

export const jstSelectionToTreePath = ({ level, paragraph, section }: JstSelection) =>
  level ? compact([paragraph && paragraph.id, section && section.id]) : void 0;

export const validateJstMasterInfo = entityValidatorByEntityConfig({
  name: { required: true },
});

export const jstClauseValidateField = fieldValidatorByEntityConfig(JST_CLAUSE_ENTITY_CONFIG);
export const jstClauseFieldState = fieldStateByEntityConfig(JST_CLAUSE_ENTITY_CONFIG);

export const setJstClauseText = composeSetters(setEntityField, fieldValidationSetter(jstClauseValidateField));

export const hasJstElementFormError = ({ info }: { info: FormStateSnapshot<any> }) =>
  hasFormStateError(info);

export const hasJstSectionInvalidClause = ({ clauses }: JstSection) =>
  clauses && clauses.some(isEntityInvalid);

export const isJstSection = (element: JstElement): element is JstSection => Object.prototype.hasOwnProperty.call(element, 'clauses');

export const hasJstElementError = (element: JstElement) =>
  hasJstElementFormError(element) || isJstSection(element) && hasJstSectionInvalidClause(element) || false;
